import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import React from 'react';
import { json } from '@remix-run/node';
import { Form, Link, useLoaderData } from '@remix-run/react';
import { motion } from 'framer-motion';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import { getDatabaseStats } from '~/lib/game-data.server';
import { H1, H2, Caption, Lead } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';
import { Button } from '~/components/ui/button';
import { staggerContainer, staggerItem, fadeUp, scaleFade } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

const DATABASE_SECTIONS = [
  {
    title: 'Skills',
    description: 'Character abilities and combat techniques broken down by class.',
    href: routes.database.skills(),
  },
  {
    title: 'Enemies',
    description: 'Encounter bestiary with locations, weaknesses, and lore.',
    href: routes.database.enemies(),
  },
  {
    title: 'Status Effects',
    description: 'Buffs, debuffs, and conditions that affect combat.',
    href: routes.database.statusEffects(),
  },
];

export const loader = async (_args: LoaderFunctionArgs) => {
  const stats = getDatabaseStats();
  return json({
    sections: DATABASE_SECTIONS,
    stats,
  });
};

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'Game Database',
    description:
      'Reference hub for items, skills, classes, and enemies within Lumora: The First Dawn.',
  });

export default function DatabaseHubPage() {
  const { sections, stats } = useLoaderData<typeof loader>();
  const { getVariants } = useAnimationState();

  return (
    <Container size="default" padding="large" className="space-y-10">
      <motion.header
        className="space-y-3 opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants(staggerContainer)}
      >
        <motion.div variants={getVariants(staggerItem)}>
          <Caption className="text-primary">Database</Caption>
        </motion.div>
        <motion.div variants={getVariants(staggerItem)}>
          <H1>Lumora Game Database</H1>
        </motion.div>
        <motion.div variants={getVariants(staggerItem)}>
          <Lead>
            Complete reference for skills, enemies, and status effects in Lumora: The First Dawn.
          </Lead>
        </motion.div>
      </motion.header>

      {/* Search Bar */}
      <motion.div
        className="rounded-lg border border-border bg-card p-6 shadow opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants({
          ...fadeUp,
          visible: {
            ...fadeUp.visible,
            transition: {
              ...(typeof fadeUp.visible === 'object' && 'transition' in fadeUp.visible
                ? fadeUp.visible.transition
                : {}),
              delay: 0.2,
            },
          },
        })}
      >
        <Form method="get" action={routes.database.search()} className="flex gap-2">
          <input
            type="search"
            name="q"
            placeholder="Search skills, enemies, status effects..."
            className="flex-1 rounded-md border border-border bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            autoComplete="off"
          />
          <Button type="submit">Search</Button>
        </Form>
      </motion.div>

      {/* Category Grid */}
      <div>
        <motion.div
          className="opacity-0"
          initial="hidden"
          animate="visible"
          variants={getVariants({
            ...fadeUp,
            visible: {
              ...fadeUp.visible,
              transition: {
                ...(typeof fadeUp.visible === 'object' && 'transition' in fadeUp.visible
                  ? fadeUp.visible.transition
                  : {}),
                delay: 0.3,
              },
            },
          })}
        >
          <H2 className="mb-4">Browse by Category</H2>
        </motion.div>
        <motion.div
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 opacity-0"
          initial="hidden"
          animate="visible"
          variants={getVariants({
            ...staggerContainer,
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
                delayChildren: 0.4,
              },
            },
          })}
        >
          {sections.map((section) => (
            <motion.div key={section.title} variants={getVariants(scaleFade)}>
              <Link
                to={section.href}
                prefetch="intent"
                className="block group rounded-lg border border-border bg-card p-6 shadow transition hover:border-primary/30 hover:shadow-lg"
              >
                <h3 className="text-xl font-semibold text-foreground">{section.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{section.description}</p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-primary transition group-hover:translate-x-1">
                  Explore →
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>

    </Container>
  );
}
